import { css } from '@emotion/react';
import { ReactNode, useRef } from 'react';
import { Currency, DonationPlan } from '../../../types/PaymentTypes';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { formatAmountAndWithDerivedLocale } from '../../../utils/helpers/currency';
import { breakpoints, colors } from '../../../utils/styleguide';
import SkeletonText from '../skeleton/SkeletonText';
import Typography from '../text/Typography';
import AmountOption from './AmountOption';
import useIsomorphicLayoutEffect from '../../../utils/hooks/useIsomorphicLayoutEffect';

type Props = {
  isSelected: boolean;
  onSelectAmount: () => void;
  disabled?: boolean;
  colorTheme?: 'default' | 'dark' | 'ppc';
};

type LabelProps = {
  amount?: number;
  currency?: Currency;
  type?: DonationPlan;
  showSkeleton?: boolean;
  isSelected: boolean;
  amountCustomLabel?: ReactNode;
};

function DonateAmount(props: Props & LabelProps): JSX.Element {
  const {
    isSelected,
    amount,
    onSelectAmount,
    type,
    currency,
    disabled,
    showSkeleton,
    colorTheme,
    amountCustomLabel,
  } = props;
  return (
    <AmountOption
      amount={amount}
      isSelected={isSelected}
      onSelectAmount={onSelectAmount}
      disabled={disabled}
      colorTheme={colorTheme}
    >
      {amountCustomLabel}
      <AmountLabel
        amount={amount}
        currency={currency}
        type={type}
        showSkeleton={showSkeleton}
        isSelected={isSelected}
      />
    </AmountOption>
  );
}

const sleep = (time = 0) => new Promise((resolve) => setTimeout(resolve, time));

async function flipAnimate(amountRef: HTMLElement, mutation: () => void) {
  amountRef.style.transition = 'none';
  await sleep(200);
  const oldPosition = amountRef.getBoundingClientRect();
  mutation();
  const newPosition = amountRef.getBoundingClientRect();
  const offsetY = newPosition.top - oldPosition.top;
  amountRef.style.transform = `translateY(${-offsetY}px)`;
  await sleep(100);
  amountRef.style.transition = 'transform 0.2s';
  amountRef.style.transform = 'translateY(0px)';
}

const AmountLabel = ({ amount, currency, type, showSkeleton, isSelected }: LabelProps) => {
  const t = useMicrocopy();
  const amountRef = useRef<HTMLElement>(null);
  const suffixRef = useRef<HTMLElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (amountRef.current === null) return;
    if (type === 'monthly') {
      flipAnimate(amountRef.current, () => {
        if (suffixRef.current) {
          suffixRef.current.style.display = 'block';
        }
      });
    } else {
      flipAnimate(amountRef.current, () => {
        if (suffixRef.current) {
          suffixRef.current.style.display = 'none';
        }
      });
    }
  }, [type]);

  return (
    <>
      <Typography
        useBuiltInMargins
        variant="buttonLarge"
        tag="span"
        css={css`
          color: inherit;
        `}
      >
        <span
          ref={amountRef}
          css={css`
            display: inline-block;
            line-height: 1;

            ${breakpoints.tablet} {
              line-height: 1;
            }
          `}
        >
          {showSkeleton ? (
            <SkeletonText>SEK 10</SkeletonText>
          ) : (
            formatAmountAndWithDerivedLocale(amount || 0, currency?.code || 'USD')
          )}
        </span>
      </Typography>
      <Typography
        variant="detailBold"
        tag="span"
        ref={suffixRef}
        css={css`
          color: ${isSelected ? colors.white : colors.darkGray};
          display: none;
          line-height: 1;

          ${breakpoints.tablet} {
            line-height: 1;
          }
        `}
      >
        {` /${t(microcopySet.PAYMENT_OPTION, 'month')}`}
      </Typography>
    </>
  );
};

export default DonateAmount;
